import { useEffect, useState } from "react";
import { Send, Flag, Trash2, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleString(undefined,
  { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export const CommentThread = ({ story, onCount }) => {
  const [rows, setRows] = useState(null);
  const [me, setMe] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/news/comments", { params: { story_id: story.story_id } });
      setRows(data.comments);
      setMe(data.me);
      onCount?.(data.comments.length);
    } catch (e) {
      console.error("could not load comments", e);
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story.story_id]);

  const post = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post("/news/comments",
        { story_id: story.story_id, title: story.title, text: body });
      setText("");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not post that");
    } finally {
      setSending(false);
    }
  };

  const report = async (row) => {
    try {
      const { data } = await api.post(`/news/comments/${row.comment_id}/report`);
      toast.success(data.hidden ? "Reported and hidden for review" : "Reported, thank you");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not report that");
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/news/comments/${row.comment_id}`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete that");
    }
  };

  return (
    <div className="mt-3 rounded-2xl border border-white/10 bg-page p-3" data-testid={`comments-${story.story_id}`}>
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-ink-soft">
        <MessageSquare className="h-3.5 w-3.5 text-brand" /> What people are saying
      </p>

      {rows === null && <p className="mt-2 text-xs text-ink-soft">Loading…</p>}
      {rows?.length === 0 && (
        <p className="mt-2 text-xs text-ink-soft">Nobody has said anything yet — go first.</p>
      )}

      <ul className="mt-2 space-y-2">
        {(rows || []).map((row) => (
          <li key={row.comment_id} className="rounded-xl border border-white/10 px-3 py-2">
            <p className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold">{row.name}</span>
              <span className="text-ink-soft">{when(row.created_at)}</span>
              {row.user_id === me ? (
                <button
                  data-testid={`comment-delete-${row.comment_id}`}
                  onClick={() => remove(row)}
                  className="ms-auto inline-flex items-center gap-1 text-ink-soft hover:text-brand"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              ) : (
                <button
                  data-testid={`comment-report-${row.comment_id}`}
                  onClick={() => report(row)}
                  className="ms-auto inline-flex items-center gap-1 text-ink-soft hover:text-brand"
                >
                  <Flag className="h-3.5 w-3.5" /> Report
                </button>
              )}
            </p>
            <p className="mt-1 text-sm" dir="auto">{row.text}</p>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          data-testid={`comment-input-${story.story_id}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && post()}
          placeholder="Say something civil…"
          maxLength={600}
          className="min-h-[38px] min-w-0 flex-1 rounded-full border border-white/15 bg-panel px-3 text-xs outline-none focus:border-brand/50"
        />
        <button
          data-testid={`comment-send-${story.story_id}`}
          onClick={post}
          disabled={sending || !text.trim()}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Post
        </button>
      </div>
    </div>
  );
};
